const coordinateLimit = 100000;
const minimumVariance = 1e-6;
const finitePoint = point => point && Number.isFinite(point.x) && Number.isFinite(point.y)
  && Math.abs(point.x) <= coordinateLimit && Math.abs(point.y) <= coordinateLimit;

// Summing relative to one reference retains precision when a small room is far
// from the Site origin. Compensation also avoids order-dependent accumulation.
function sum(values) {
  let total = 0, correction = 0;
  for (const value of values) {
    const adjusted = value - correction;
    const next = total + adjusted;
    correction = (next - total) - adjusted;
    total = next;
  }
  return total;
}

function centred(points) {
  const origin = points[0];
  const centre = {
    x: origin.x + sum(points.map(point => point.x - origin.x)) / points.length,
    y: origin.y + sum(points.map(point => point.y - origin.y)) / points.length,
  };
  const offsets = points.map(point => ({ x: point.x - centre.x, y: point.y - centre.y }));
  const spread = sum(offsets.map(point => point.x * point.x + point.y * point.y));
  if (spread / points.length < minimumVariance) {
    throw new Error('Reference corners are too close together. Choose corners farther apart.');
  }
  return { centre, offsets, spread };
}

/** Fit a rigid placement in metres, using scanGeometry's rotation convention.
 * No scaling or reflection is permitted. Residuals remain available even when
 * the fit is poor; callers must review them before accepting placement. Two
 * references constrain a fit but cannot independently validate the scan shape.
 */
export function alignScanReferences(pairs, { tolerance = 0.05 } = {}) {
  if (!Array.isArray(pairs) || pairs.length < 2 || pairs.length > 4000) {
    throw new Error('Choose between 2 and 4000 matching reference corners.');
  }
  if (!Number.isFinite(tolerance) || tolerance < 0 || tolerance > coordinateLimit) {
    throw new Error('Enter a finite, nonnegative alignment tolerance in metres.');
  }
  if (pairs.some(pair => !finitePoint(pair?.source) || !finitePoint(pair?.target))) {
    throw new Error('Each reference needs valid scan and Site coordinates within 100000 metres of the origin.');
  }
  const source = centred(pairs.map(pair => pair.source));
  const target = centred(pairs.map(pair => pair.target));
  const dot = sum(source.offsets.map((point, index) => point.x * target.offsets[index].x + point.y * target.offsets[index].y));
  const cross = sum(source.offsets.map((point, index) => point.x * target.offsets[index].y - point.y * target.offsets[index].x));
  // A symmetric reflected set can leave every rotation equally poor. Its
  // rotation is not determined, even when absolute residuals would be small.
  const ambiguous = Math.hypot(dot, cross) <= Number.EPSILON * 16 * Math.sqrt(source.spread * target.spread);
  if (ambiguous) throw new Error('These matches do not determine a reliable rotation. Check the point order.');
  const radians = Math.atan2(cross, dot);
  const cos = Math.cos(radians), sin = Math.sin(radians);
  const x = target.centre.x - (source.centre.x * cos - source.centre.y * sin);
  const y = target.centre.y - (source.centre.x * sin + source.centre.y * cos);
  const residuals = pairs.map(pair => Math.hypot(
    x + pair.source.x * cos - pair.source.y * sin - pair.target.x,
    y + pair.source.x * sin + pair.source.y * cos - pair.target.y,
  ));
  const rms = Math.sqrt(sum(residuals.map(distance => distance * distance)) / pairs.length);
  const maxResidual = Math.max(...residuals);
  return { x, y, angle: radians * 180 / Math.PI, residuals, rms, maxResidual, withinTolerance: maxResidual <= tolerance };
}

/** Offer one further correspondence after two operator-selected anchors. The
 * proposal is only a shortcut for filling the review form: repeated geometry
 * can look identical, so it never authorizes placement or changes the fit. */
export function suggestAdditionalScanReference(source, target, pairs, fit, { tolerance = 0.05 } = {}) {
  if (!Array.isArray(source) || !Array.isArray(target) || source.length > 256 || target.length > 10000)
    throw new Error('This scan has too many corners for automatic match suggestions. Choose another point manually.');
  if (!Array.isArray(pairs) || pairs.length !== 2 || !fit?.withinTolerance ||
      !Number.isFinite(fit.x) || !Number.isFinite(fit.y) || !Number.isFinite(fit.angle) ||
      !Number.isFinite(tolerance) || tolerance < 0 || tolerance > 1)
    throw new Error('Match two valid scan and Site points before suggesting another.');
  if (source.some(p => !finitePoint(p)) || target.some(p => !finitePoint(p)))
    throw new Error('Scan and Site corners need valid coordinates before matching.');
  const a = pairs[0].source, b = pairs[1].source;
  if (!finitePoint(a) || !finitePoint(b) || pairs.some(pair => !finitePoint(pair.target)))
    throw new Error('Match two valid scan and Site points before suggesting another.');
  const baseline = Math.hypot(b.x - a.x, b.y - a.y);
  if (baseline < 0.1) return null;
  const minimumOffset = Math.min(1, Math.max(0.25, baseline * 0.1));
  const searchRadius = Math.min(1, Math.max(0.15, tolerance * 4));
  const angle = fit.angle * Math.PI / 180, cos = Math.cos(angle), sin = Math.sin(angle);
  const used = (point, side) => pairs.some(pair => Math.hypot(point.x - pair[side].x, point.y - pair[side].y) < .001);
  const cell = (x, y) => `${Math.floor(x / searchRadius)},${Math.floor(y / searchRadius)}`;
  const nearby = new Map();
  for (let targetIndex = 0; targetIndex < target.length; targetIndex++) {
    if (used(target[targetIndex], 'target')) continue;
    const key = cell(target[targetIndex].x, target[targetIndex].y);
    if (!nearby.has(key)) nearby.set(key, []);
    nearby.get(key).push(targetIndex);
  }
  const candidates = [];
  for (let sourceIndex = 0; sourceIndex < source.length; sourceIndex++) {
    const point = source[sourceIndex];
    if (used(point, 'source')) continue;
    const offset = Math.abs((b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x)) / baseline;
    if (offset < minimumOffset) continue;
    const x = fit.x + point.x * cos - point.y * sin, y = fit.y + point.x * sin + point.y * cos;
    const gx = Math.floor(x / searchRadius), gy = Math.floor(y / searchRadius);
    for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++)
      for (const targetIndex of nearby.get(`${gx + dx},${gy + dy}`) || []) {
        const sitePoint = target[targetIndex], error = Math.hypot(x - sitePoint.x, y - sitePoint.y);
        if (error <= searchRadius) candidates.push({ sourceIndex, targetIndex, error, offset });
      }
  }
  const sourceCounts = new Map(), targetCounts = new Map();
  for (const candidate of candidates) {
    sourceCounts.set(candidate.sourceIndex, (sourceCounts.get(candidate.sourceIndex) || 0) + 1);
    targetCounts.set(candidate.targetIndex, (targetCounts.get(candidate.targetIndex) || 0) + 1);
  }
  const distinct = candidates.filter(candidate => sourceCounts.get(candidate.sourceIndex) === 1 && targetCounts.get(candidate.targetIndex) === 1);
  distinct.sort((a, b) => b.offset - a.offset || a.error - b.error || a.sourceIndex - b.sourceIndex);
  return distinct[0] ? { ...distinct[0], searchRadius } : null;
}
